import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Truck, 
  Save, 
  TestTube, 
  CheckCircle, 
  XCircle,
  Loader2,
  Plus,
  Trash2,
  Edit2,
  IndianRupee,
  Star,
  MoreVertical
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DTDCIntegrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    api_key: '',
    customer_code: '',
    tracking_token: '',
    test_mode: false,
    default_warehouse_id: '',
    cod_enabled: true,
    service_type: 'B2C SMART EXPRESS',
    label_format: 'a4',
    auto_pickup: true,
    custom_printable_text: ''
  });

  // Label formats
  const labelFormats = [
    { value: 'a4', label: 'A4 (Full Page)' },
    { value: 'a6', label: 'A6 (4x6 inches)' },
    { value: '4x6', label: '4x6 Thermal' },
    { value: '100x150', label: '100x150mm' }
  ];

  useEffect(() => {
    fetchIntegrations();
    fetchWarehouses();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/couriers/integrations/dtdc`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setIntegrations(response.data.integrations || []);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      const token = localStorage.getItem('token');
      // Fetch from enterprise locations endpoint
      const response = await axios.get(`${API_BASE_URL}/enterprise/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWarehouses(response.data.locations || []);
    } catch (error) {
      console.error('Error fetching warehouses:', error);
    }
  };

  const openAddDialog = () => {
    setEditingIntegration(null);
    setFormData({
      name: '',
      api_key: '',
      customer_code: '',
      tracking_token: '',
      test_mode: false,
      default_warehouse_id: '',
      cod_enabled: true,
      service_type: 'B2C SMART EXPRESS'
    });
    setTestResult(null);
    setDialogOpen(true);
  };

  const openEditDialog = (integration) => {
    setEditingIntegration(integration);
    setFormData({
      name: integration.name || '',
      api_key: integration.credentials?.api_key || '',
      customer_code: integration.credentials?.customer_code || '',
      tracking_token: integration.credentials?.tracking_token || '',
      test_mode: integration.test_mode ?? false,
      default_warehouse_id: integration.settings?.default_warehouse_id || '',
      cod_enabled: integration.settings?.cod_enabled ?? true,
      service_type: integration.settings?.service_type || 'B2C SMART EXPRESS',
      label_format: integration.settings?.label_format || 'a4',
      auto_pickup: integration.settings?.auto_pickup !== false,
      custom_printable_text: integration.settings?.custom_printable_text || ''
    });
    setTestResult(null);
    setDialogOpen(true);
  };

  const handleTestConnection = async () => {
    if (!formData.api_key || !formData.customer_code) {
      toast.error('Please enter API Key and Customer Code');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/couriers/test-connection`,
        {
          courier_type: 'dtdc',
          credentials: {
            api_key: formData.api_key,
            customer_code: formData.customer_code,
            tracking_token: formData.tracking_token
          },
          test_mode: formData.test_mode
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTestResult({
        success: response.data.success,
        message: response.data.message || 'Connection successful'
      });
      
      if (response.data.success) {
        toast.success('Connection test passed!');
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error.response?.data?.detail || 'Connection failed'
      });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.api_key || !formData.customer_code) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        name: formData.name,
        courier_type: 'dtdc',
        credentials: {
          api_key: formData.api_key,
          customer_code: formData.customer_code,
          tracking_token: formData.tracking_token
        },
        test_mode: formData.test_mode,
        settings: {
          default_warehouse_id: formData.default_warehouse_id,
          cod_enabled: formData.cod_enabled,
          service_type: formData.service_type,
          label_format: formData.label_format,
          auto_pickup: formData.auto_pickup,
          custom_printable_text: formData.custom_printable_text?.trim() || null
        }
      };

      if (editingIntegration) {
        await axios.put(
          `${API_BASE_URL}/couriers/integrations/${editingIntegration.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Integration updated successfully');
      } else {
        await axios.post(
          `${API_BASE_URL}/couriers/integrations`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Integration added successfully');
      }

      setDialogOpen(false);
      fetchIntegrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (integrationId) => {
    if (!window.confirm('Are you sure you want to delete this integration?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE_URL}/couriers/integrations/${integrationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Integration deleted');
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to delete integration');
    }
  };

  const handleSetDefault = async (integrationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/couriers/integrations/${integrationId}/set-default`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Default integration updated');
      fetchIntegrations();
    } catch (error) {
      toast.error('Failed to set default');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/integrations')}
          className="hover:bg-wt-subtle"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-wt-text-primary flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600">
              <Truck className="h-6 w-6 text-white" />
            </div>
            DTDC Integration
          </h1>
          <p className="text-wt-text-muted mt-1">
            Configure DTDC courier accounts for nationwide delivery
          </p>
        </div>
        <Button 
          onClick={openAddDialog}
          className="bg-wt-gold hover:bg-wt-gold-dark text-black"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Account
        </Button>
      </div>

      {/* Integration Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-wt-gold" />
        </div>
      ) : integrations.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-surface border border-wt-border rounded-xl p-12 text-center"
        >
          <Truck className="h-16 w-16 text-wt-text-muted mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-wt-text-primary mb-2">No DTDC Accounts</h3>
          <p className="text-wt-text-muted mb-6">
            Add your DTDC account to start booking shipments across India
          </p>
          <Button onClick={openAddDialog} className="bg-wt-gold hover:bg-wt-gold-dark text-black">
            <Plus className="h-4 w-4 mr-2" />
            Add DTDC Account
          </Button>
        </motion.div>
      ) : (
        <div className="grid gap-4">
          {integrations.map((integration) => (
            <motion.div
              key={integration.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-surface border border-wt-border rounded-xl p-6"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600">
                    <Truck className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-wt-text-primary">{integration.name}</h3>
                      {integration.is_default && (
                        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          <Star className="h-3 w-3 mr-1" /> Default
                        </Badge>
                      )}
                      {integration.test_mode && (
                        <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">
                          Test Mode
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-wt-text-muted mt-1">
                      Customer Code: {integration.credentials?.customer_code || '***'}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm flex-wrap">
                      <span className="flex items-center gap-1 text-green-400">
                        <CheckCircle className="h-4 w-4" />
                        {integration.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {integration.settings?.service_type && (
                        <span className="text-wt-text-muted">
                          Service: {integration.settings.service_type}
                        </span>
                      )}
                      {integration.settings?.label_format && (
                        <span className="text-cyan-400">
                          {labelFormats.find(f => f.value === integration.settings.label_format)?.label || integration.settings.label_format}
                        </span>
                      )}
                      {integration.settings?.cod_enabled && (
                        <span className="text-wt-text-muted">COD Enabled</span>
                      )}
                      {integration.settings?.auto_pickup && (
                        <span className="text-green-400">Auto Pickup</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-wt-surface border-wt-border">
                    <DropdownMenuItem onClick={() => openEditDialog(integration)}>
                      <Edit2 className="h-4 w-4 mr-2" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate(`/integrations/shipping-rate-cards?open=${integration.id}`)} data-testid={`dtdc-rate-card-${integration.id}`}>
                      <IndianRupee className="h-4 w-4 mr-2" /> Rate Card
                    </DropdownMenuItem>
                    {!integration.is_default && (
                      <DropdownMenuItem onClick={() => handleSetDefault(integration.id)}>
                        <Star className="h-4 w-4 mr-2" /> Set as Default
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem 
                      onClick={() => handleDelete(integration.id)}
                      className="text-red-400"
                    >
                      <Trash2 className="h-4 w-4 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-wt-paper border-wt-border max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-wt-text-primary">
              {editingIntegration ? 'Edit DTDC Account' : 'Add DTDC Account'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4 overflow-y-auto flex-1 pr-2">
            <div>
              <Label className="text-wt-text-primary">Account Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., DTDC Main Account"
                className="mt-1 bg-wt-bg border-wt-border"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">API Key *</Label>
              <Input
                value={formData.api_key}
                onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                placeholder="Your DTDC API Key"
                className="mt-1 bg-wt-bg border-wt-border"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                Contact your DTDC account manager for API credentials
              </p>
            </div>

            <div>
              <Label className="text-wt-text-primary">Customer Code *</Label>
              <Input
                value={formData.customer_code}
                onChange={(e) => setFormData({ ...formData, customer_code: e.target.value })}
                placeholder="Your DTDC Customer Code"
                className="mt-1 bg-wt-bg border-wt-border"
              />
            </div>

            <div>
              <Label className="text-wt-text-primary">Tracking Access Token</Label>
              <Input
                value={formData.tracking_token}
                onChange={(e) => setFormData({ ...formData, tracking_token: e.target.value })}
                placeholder="e.g., UO2885_trk_json:xxxxx"
                className="mt-1 bg-wt-bg border-wt-border"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                Used for tracking API. Format: CUSTOMER_CODE_trk_json:token
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Default Warehouse</Label>
                <Select
                  value={formData.default_warehouse_id}
                  onValueChange={(value) => setFormData({ ...formData, default_warehouse_id: value })}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border max-h-[200px]">
                    {warehouses.length === 0 ? (
                      <SelectItem value="no-warehouse" disabled>No warehouses found</SelectItem>
                    ) : (
                      warehouses.map((w) => (
                        <SelectItem key={w.id} value={w.id || `warehouse-${w.name}`}>
                          <div className="flex flex-col">
                            <span className="font-medium">{w.name}</span>
                            {(w.zip || w.city) && (
                              <span className="text-xs text-wt-text-muted">
                                {w.city}{w.city && w.zip && ', '}{w.zip}
                              </span>
                            )}
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-wt-text-primary">Service Type</Label>
                <Select
                  value={formData.service_type}
                  onValueChange={(value) => setFormData({ ...formData, service_type: value })}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-paper border-wt-border">
                    <SelectItem value="B2C SMART EXPRESS">B2C Smart Express</SelectItem>
                    <SelectItem value="LITE">Lite (Economy)</SelectItem>
                    <SelectItem value="PRIORITY">Priority</SelectItem>
                    <SelectItem value="GROUND">Ground</SelectItem>
                    <SelectItem value="EXPRESS">Express</SelectItem>
                    <SelectItem value="SURFACE EXPRESS">Surface Express</SelectItem>
                    <SelectItem value="B2C PLUS">B2C Plus</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-wt-text-muted mt-1">
                  Select the service type enabled for your DTDC account
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.test_mode}
                  onCheckedChange={(checked) => setFormData({ ...formData, test_mode: checked })}
                />
                <Label className="text-wt-text-primary">Test Mode</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.cod_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, cod_enabled: checked })}
                />
                <Label className="text-wt-text-primary">COD Enabled</Label>
              </div>
            </div>

            {/* Label Format and Auto Pickup */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-wt-text-primary">Label Format</Label>
                <Select 
                  value={formData.label_format} 
                  onValueChange={(v) => setFormData({ ...formData, label_format: v })}
                >
                  <SelectTrigger className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wt-surface border-wt-border">
                    {labelFormats.map((format) => (
                      <SelectItem key={format.value} value={format.value} className="text-wt-text-primary hover:bg-wt-subtle">
                        {format.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={formData.auto_pickup}
                  onCheckedChange={(checked) => setFormData({ ...formData, auto_pickup: checked })}
                />
                <Label className="text-wt-text-primary">Auto Schedule Pickup</Label>
              </div>
            </div>

            {/* Custom Printable Text */}
            <div>
              <Label className="text-wt-text-primary">Custom Printable Text</Label>
              <Input
                type="text"
                value={formData.custom_printable_text}
                onChange={(e) => setFormData({ ...formData, custom_printable_text: e.target.value })}
                placeholder="e.g., Handle with care, Fragile"
                maxLength={150}
                className="mt-1 bg-wt-bg border-wt-border text-wt-text-primary"
              />
              <p className="text-xs text-wt-text-muted mt-1">
                This text will appear on the shipping label. Max 150 characters. ({formData.custom_printable_text?.length || 0}/150)
              </p>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`p-3 rounded-lg ${testResult.success ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-400" />
                  )}
                  <span className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                    {testResult.message}
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2 flex-shrink-0 pt-4 border-t border-wt-border">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || !formData.api_key}
              className="border-wt-border"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <TestTube className="h-4 w-4 mr-2" />
              )}
              Test Connection
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-wt-gold hover:bg-wt-gold-dark text-black"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {editingIntegration ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DTDCIntegrationPage;
